// QueueTaskDetailDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { Bot, Users, Brain, Cpu, Sparkles, AlertTriangle, ChevronDown, CheckCircle, XCircle } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { TASK_STATUS_COLORS } from '../constants';

/**
 * Dialog for viewing detailed queue task information with AI results
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.task - The selected queue task to display
 * @param {function} props.onApprove - Callback when approve is clicked (taskId, isApproved)
 * @param {function} props.onClose - Callback when close is clicked
 */
export const QueueTaskDetailDialog = ({
  open,
  onOpenChange,
  task,
  onApprove,
  onClose
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {task?.assigned_agent_id ? (
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
            ) : (
              <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                <Users className="h-5 w-5 text-slate-600" />
              </div>
            )}
            <div>
              <span>{task?.title}</span>
              <div className="flex gap-2 mt-1">
                <Badge className={TASK_STATUS_COLORS[task?.status || 'new']}>
                  {task?.status?.replace('_', ' ')}
                </Badge>
                <Badge variant="outline">{task?.task_type}</Badge>
                <Badge variant="outline">P{task?.priority}</Badge>
              </div>
            </div>
          </DialogTitle>
          <DialogDescription>
            Queue: {task?.queue_name} • Created {task?.created_at ? formatDistanceToNow(new Date(task.created_at), { addSuffix: true }) : ''}
          </DialogDescription>
        </DialogHeader>
        
        {task && (
          <div className="space-y-6 py-4">
            {/* Task Details */}
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <p className="mt-1 text-sm text-muted-foreground bg-slate-50 p-3 rounded-lg">
                {task.description}
              </p>
            </div>

            {/* Assignment Info */}
            {task.assigned_agent_name && (
              <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg">
                <Bot className="h-5 w-5 text-violet-600" />
                <div>
                  <p className="font-medium text-violet-700">Assigned to: {task.assigned_agent_name}</p>
                  <p className="text-sm text-violet-600">AI Agent</p>
                </div>
              </div>
            )}

            {/* Risk Assessment */}
            {task.risk_level && task.risk_level !== 'low' && (
              <div className={`p-3 rounded-lg ${
                task.risk_level === 'critical' ? 'bg-red-50 border border-red-200' :
                task.risk_level === 'high' ? 'bg-orange-50 border border-orange-200' :
                'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`h-4 w-4 ${
                    task.risk_level === 'critical' ? 'text-red-600' :
                    task.risk_level === 'high' ? 'text-orange-600' : 'text-amber-600'
                  }`} />
                  <span className="font-medium">Risk Level: {task.risk_level}</span>
                </div>
                {task.exception_flags?.length > 0 && (
                  <p className="mt-1 text-sm">Flags: {task.exception_flags.join(', ')}</p>
                )}
              </div>
            )}

            {/* AI Processing Results (if available) */}
            {task.ai_result && (
              <div className="space-y-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Brain className="h-4 w-4" />
                  AI Processing Results
                </Label>
                
                {/* Left Brain */}
                <Collapsible>
                  <CollapsibleTrigger className="w-full p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 hover:bg-blue-100 transition-colors">
                    <Cpu className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-700">Left Brain Analysis</span>
                    <ChevronDown className="h-4 w-4 text-blue-600 ml-auto" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <ScrollArea className="max-h-[300px] bg-blue-50/50 border border-blue-100 rounded-lg p-4">
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown>{task.ai_result?.left_brain || 'No data'}</ReactMarkdown>
                      </div>
                    </ScrollArea>
                  </CollapsibleContent>
                </Collapsible>

                {/* Right Brain */}
                <Collapsible>
                  <CollapsibleTrigger className="w-full p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2 hover:bg-purple-100 transition-colors">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    <span className="font-medium text-purple-700">Right Brain Analysis</span>
                    <ChevronDown className="h-4 w-4 text-purple-600 ml-auto" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <ScrollArea className="max-h-[300px] bg-purple-50/50 border border-purple-100 rounded-lg p-4">
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown>{task.ai_result?.right_brain || 'No data'}</ReactMarkdown>
                      </div>
                    </ScrollArea>
                  </CollapsibleContent>
                </Collapsible>

                {/* Cortex */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="w-full p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 hover:bg-emerald-100 transition-colors">
                    <Brain className="h-4 w-4 text-emerald-600" />
                    <span className="font-medium text-emerald-700">Cortex Synthesis</span>
                    <Badge className="ml-2 bg-emerald-500 text-white text-xs">Final Output</Badge>
                    <ChevronDown className="h-4 w-4 text-emerald-600 ml-auto" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <ScrollArea className="max-h-[400px] bg-emerald-50/50 border border-emerald-100 rounded-lg p-4">
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown>{task.ai_result?.cortex || 'No data'}</ReactMarkdown>
                      </div>
                    </ScrollArea>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}

            {/* Status History */}
            {task.status_history?.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Status History</Label>
                <div className="mt-2 space-y-2">
                  {task.status_history.map((entry, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <div className="h-2 w-2 rounded-full bg-slate-400" />
                      <Badge className={TASK_STATUS_COLORS[entry.status]}>{entry.status}</Badge>
                      <span className="text-muted-foreground">
                        {entry.timestamp ? format(new Date(entry.timestamp), 'MMM d, HH:mm') : ''}
                      </span>
                      {entry.note && <span className="text-muted-foreground">— {entry.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="queue-task-close">
            Close
          </Button>
          {task?.status === 'awaiting_approval' && (
            <>
              <Button 
                variant="outline" 
                onClick={() => { onApprove(task.id, false); onClose(); }}
                data-testid="queue-task-reject"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button 
                onClick={() => { onApprove(task.id, true); onClose(); }}
                data-testid="queue-task-approve"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QueueTaskDetailDialog;
